import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  AlertTriangle, ArrowRight, BellOff, CheckCircle2, Inbox, Loader2, Mail, RefreshCw,
  Send, Sparkles, XCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatInTz, propertyTimezone } from "@/lib/timezone";
import {
  FLAG_LABELS, TABS, canCreateClient, clientPayload, displayName, initialClientChoice,
  money, tabLabel, unlistedPrices, userOptions,
} from "@/lib/prospectsView";
import { tr } from "@/lib/i18n";
import { trRich } from "@/lib/i18nRich";

/**
 * Prospects — the sales inbox. Newsdesk reads each rep's connected
 * mailbox (Settings → Sales inbox), keeps the emails that look like ad
 * prospects, drafts a reply from the PublishOS offer, and a person sends
 * it. Every email in and out is logged to the client in PublishOS.
 *
 * Nothing is sent without pressing Send.
 */
export default function ProspectsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [sp, setSp] = useSearchParams();
  const [tab, setTab] = useState("needs_reply");
  const [userFilter, setUserFilter] = useState("");
  const selectedId = sp.get("thread") || "";

  const { data: settings } = useQuery({
    queryKey: ["settings"], queryFn: api.getSettings, staleTime: 5 * 60 * 1000,
  });
  const tz = propertyTimezone(settings);

  const setup = useQuery({ queryKey: ["prospects-setup"], queryFn: api.prospectsSetup, staleTime: 60 * 1000 });
  const counts = useQuery({
    queryKey: ["prospects-counts", userFilter],
    queryFn: () => api.prospectsCounts(userFilter),
    refetchInterval: 120 * 1000,
  });
  const threads = useQuery({
    queryKey: ["prospects-threads", tab, userFilter],
    queryFn: () => api.prospectsThreads(tab, userFilter),
    refetchInterval: 120 * 1000,
  });

  const users = useMemo(() => userOptions(setup.data?.all_connections), [setup.data]);
  const conn = setup.data?.connection;
  const rows = threads.data || [];

  const select = (id) => {
    const next = new URLSearchParams(sp);
    if (id) next.set("thread", id);
    else next.delete("thread");
    setSp(next, { replace: true });
  };

  return (
    <div className="flex-1 overflow-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        <div className="mb-6">
          <div className="overline mb-2">{tr("Sales")}</div>
          <div className="flex items-baseline gap-4 flex-wrap">
            <h1 className="font-serif-display text-4xl sm:text-5xl tracking-tight">{tr("Prospects")}</h1>
            <button
              data-testid="prospects-refresh"
              onClick={() => { counts.refetch(); threads.refetch(); }}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-[var(--ink-muted)] hover:text-[var(--ink)] border border-[var(--border)]"
            >
              <RefreshCw className={`w-3 h-3 ${threads.isFetching ? "animate-spin" : ""}`} />{tr("Refresh")}</button>
          </div>
          <p className="mt-3 text-sm text-[var(--ink-muted)] max-w-2xl">{tr("Ad prospects found in your sales mailbox, with a reply drafted from the PublishOS offer. Nothing is sent until you press Send, and every email is logged to the client in PublishOS.")}</p>
        </div>

        {setup.data && !conn && (
          <div data-testid="prospects-no-mailbox" className="mb-4 border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">{tr("You haven't connected a sales mailbox on this property yet.")}{" "}
            <Link to="/settings#sales-inbox" className="underline font-medium">{tr("Connect it in Settings → Sales inbox")}</Link>.
          </div>
        )}
        {conn && conn.status === "reconnect" && (
          <div className="mb-4 border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
            {conn.address}: {conn.error || tr("Reconnect needed.")}{" "}
            <Link to="/settings#sales-inbox" className="underline font-medium">{tr("Reconnect in Settings")}</Link>.
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 mb-4">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`prospects-tab-${t.key}`}
              onClick={() => { setTab(t.key); select(""); }}
              className={`px-3 py-1.5 text-xs border ${
                tab === t.key
                  ? "bg-[var(--ink)] text-white border-[var(--ink)]"
                  : "border-[var(--border)] text-[var(--ink)] hover:bg-[var(--secondary)]"
              }`}
            >
              {t.label}
              {counts.data?.[t.key] ? <span className="ml-1.5 font-mono-display">{counts.data[t.key]}</span> : null}
            </button>
          ))}
          {isAdmin && users.length > 1 && (
            <select
              data-testid="prospects-user-filter"
              value={userFilter}
              onChange={(e) => { setUserFilter(e.target.value); select(""); }}
              className="ml-auto text-xs border border-[var(--border)] bg-white px-2 py-1.5"
            >
              <option value="">{tr("Everyone")}</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.label}</option>)}
            </select>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4">
          <div className="bg-white border border-[var(--border)] self-start">
            {threads.isLoading ? (
              <div className="p-4 text-xs text-[var(--ink-muted)] flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />{" "}{tr("Loading…")}</div>
            ) : rows.length === 0 ? (
              <div className="p-6 text-sm text-[var(--ink-muted)] text-center" data-testid="prospects-empty">
                <Inbox className="w-5 h-5 mx-auto mb-2" />{tr("Nothing in {tabLabel}.", { tabLabel: tabLabel(tab) })}</div>
            ) : (
              rows.map((t) => (
                <button
                  key={t.id}
                  data-testid={`prospect-row-${t.id}`}
                  onClick={() => select(t.id)}
                  className={`w-full text-left px-4 py-3 border-b border-[var(--border)] last:border-b-0 ${
                    selectedId === t.id ? "bg-[var(--secondary)]" : "hover:bg-[var(--secondary)]"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium truncate flex-1">{displayName(t)}</span>
                    <span className="text-[11px] text-[var(--ink-muted)] shrink-0">
                      {formatInTz(t.last_activity_at, tz, "compact")}
                    </span>
                  </div>
                  <div className="text-xs truncate mt-0.5">{t.subject || tr("(no subject)")}</div>
                  <div className="text-xs text-[var(--ink-muted)] truncate mt-0.5">{t.summary}</div>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    <span className="text-[10px] font-mono-display uppercase px-1.5 py-0.5 border border-[var(--border)]">
                      {t.language || tr("en")}
                    </span>
                    {t.publishos_client && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-800 border border-emerald-200">
                        {t.is_advertiser ? tr("Advertiser") : tr("In PublishOS")}
                      </span>
                    )}
                    {t.draft_status === "ready" && t.tab === "needs_reply" && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-sky-50 text-sky-800 border border-sky-200">{tr("Draft ready")}</span>
                    )}
                    {t.publishos_error && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-red-50 text-red-700 border border-red-200">{tr("PublishOS")}</span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>

          <div>
            {selectedId ? (
              <ThreadPanel key={selectedId} threadId={selectedId} tz={tz} onClose={() => select("")} />
            ) : (
              <div className="bg-white border border-[var(--border)] p-10 text-center text-sm text-[var(--ink-muted)]">
                <Mail className="w-5 h-5 mx-auto mb-2" />{tr("Pick a conversation on the left.")}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ThreadPanel({ threadId, tz, onClose }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["prospect-thread", threadId], queryFn: () => api.prospectsThread(threadId) });
  const data = q.data;
  const thread = data?.thread;
  const draft = data?.draft;

  const [body, setBody] = useState("");
  const [instruction, setInstruction] = useState("");
  const [choice, setChoice] = useState(null);
  const [outcome, setOutcome] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [pricesOk, setPricesOk] = useState(false);
  const [ignoreSender, setIgnoreSender] = useState(false);
  const autoDrafted = useRef(false);
  const loadedDraft = useRef("");

  const refreshAll = () => {
    qc.invalidateQueries({ queryKey: ["prospect-thread", threadId] });
    qc.invalidateQueries({ queryKey: ["prospects-threads"] });
    qc.invalidateQueries({ queryKey: ["prospects-counts"] });
  };

  // Load the latest draft into the editor once per draft version.
  useEffect(() => {
    if (draft && loadedDraft.current !== draft.id) {
      loadedDraft.current = draft.id;
      setBody(draft.body || "");
      setPricesOk(false);
    }
  }, [draft]);

  useEffect(() => {
    if (data && choice === null) {
      setChoice(initialClientChoice(data.thread, data.match, data.draft));
      setOutcome(data.suggested_outcome || "contacted");
      setFollowUp(data.default_follow_up || "");
    }
  }, [data, choice]);

  const drafting = useMutation({
    mutationFn: (instr) => api.prospectsDraft(threadId, instr || ""),
    onSuccess: () => { setInstruction(""); refreshAll(); },
    onError: (e) => toast.error(e?.response?.data?.detail || tr("Draft failed")),
  });

  // A Needs-reply thread opened before the background draft finished
  // gets its draft now.
  useEffect(() => {
    if (thread && !draft && !autoDrafted.current && thread.tab === "needs_reply" &&
        thread.draft_status !== "working") {
      autoDrafted.current = true;
      drafting.mutate("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread, draft]);

  const send = useMutation({
    mutationFn: () => api.prospectsSend(threadId, {
      body,
      outcome,
      follow_up_date: outcome === "not_interested" ? null : followUp || null,
      allow_unlisted_prices: pricesOk,
      client: clientPayload(choice),
    }),
    onSuccess: (res) => {
      toast.success(tr("Sent"));
      if (res?.publishos_warning) toast.warning(res.publishos_warning, { duration: 10000 });
      refreshAll();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || tr("Not sent")),
  });

  const move = useMutation({
    mutationFn: ({ tab, ignore }) => api.prospectsMove(threadId, tab, ignore),
    onSuccess: () => { refreshAll(); onClose(); },
    onError: (e) => toast.error(e?.response?.data?.detail || tr("Couldn't move it")),
  });
  const snooze = useMutation({
    mutationFn: (days) => api.prospectsSnooze(threadId, days),
    onSuccess: () => { toast.success(tr("Snoozed")); refreshAll(); onClose(); },
  });
  const retry = useMutation({
    mutationFn: () => api.prospectsRetryPublishos(threadId),
    onSuccess: () => { toast.success(tr("Logged in PublishOS")); refreshAll(); },
    onError: (e) => toast.error(e?.response?.data?.detail || tr("PublishOS still failing")),
  });

  if (q.isLoading) {
    return (
      <div className="bg-white border border-[var(--border)] p-6 text-xs text-[var(--ink-muted)] flex items-center gap-2">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />{" "}{tr("Loading…")}</div>
    );
  }
  if (!data) {
    return <div className="bg-white border border-[var(--border)] p-6 text-sm">{tr("This conversation is not available.")}</div>;
  }

  const offer = data.offer || { products: [], packages: [] };
  const badPrices = unlistedPrices(body, offer);
  const offerById = {};
  for (const p of [...(offer.products || []), ...(offer.packages || [])]) offerById[p.id] = p;
  const connection = data.connection;
  const sendDisabled =
    send.isPending || !body.trim() || !connection || (badPrices.length > 0 && !pricesOk) ||
    !canCreateClient(choice);
  const client = thread.publishos_client;

  return (
    <div className="space-y-4" data-testid="prospect-thread">
      <div className="bg-white border border-[var(--border)] p-4">
        <div className="flex flex-wrap items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="font-serif-display text-xl truncate">{displayName(thread)}</div>
            <div className="text-xs text-[var(--ink-muted)]">
              {thread.prospect_email} · {thread.subject || tr("(no subject)")} · {tabLabel(thread.tab)}
            </div>
            {thread.summary && <div className="text-sm mt-2">{thread.summary}</div>}
          </div>
          <div className="flex flex-wrap gap-2">
            {thread.tab !== "needs_reply" && thread.tab !== "won" && (
              <button
                data-testid="prospect-move-needs-reply"
                onClick={() => move.mutate({ tab: "needs_reply", ignore: false })}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs border border-[var(--border)] hover:bg-[var(--secondary)]"
              >
                <ArrowRight className="w-3.5 h-3.5" />{" "}{tr("It's a prospect")}</button>
            )}
            {thread.tab === "needs_reply" && (
              <button
                data-testid="prospect-snooze"
                onClick={() => snooze.mutate(3)}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs border border-[var(--border)] hover:bg-[var(--secondary)]"
              >
                <BellOff className="w-3.5 h-3.5" />{" "}{tr("Snooze 3 days")}</button>
            )}
            {thread.tab !== "dismissed" && (
              <span className="inline-flex items-center gap-2">
                <label className="text-[11px] text-[var(--ink-muted)] flex items-center gap-1">
                  <input type="checkbox" checked={ignoreSender} onChange={(e) => setIgnoreSender(e.target.checked)} />{tr("ignore this sender")}</label>
                <button
                  data-testid="prospect-dismiss"
                  onClick={() => move.mutate({ tab: "dismissed", ignore: ignoreSender })}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs border border-[var(--border)] hover:bg-[var(--secondary)]"
                >
                  <XCircle className="w-3.5 h-3.5" />{" "}{tr("Not a prospect")}</button>
              </span>
            )}
          </div>
        </div>
        {data.publishos_error && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-red-700 bg-red-50 border border-red-200 px-2.5 py-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />
            {trRich("<0>PublishOS: {publishos_error}</0>", { publishos_error: data.publishos_error }, [<span className="flex-1" />])}
            {thread.publishos_client_id && (
              <button
                data-testid="prospect-retry-publishos"
                onClick={() => retry.mutate()}
                disabled={retry.isPending}
                className="underline font-medium"
              >{tr("Retry PublishOS")}</button>
            )}
          </div>
        )}
      </div>

      <div className="bg-white border border-[var(--border)] divide-y divide-[var(--border)]">
        {(data.messages || []).map((m) => (
          <div key={m.id} className={`p-4 ${m.direction === "out" ? "bg-[var(--secondary)]" : ""}`}>
            <div className="flex items-center gap-2 text-xs text-[var(--ink-muted)] mb-1.5">
              <span className="font-medium text-[var(--ink)]">
                {m.direction === "out" ? tr("You ({from_email})", { from_email: m.from_email }) : m.from_name || m.from_email}
              </span>
              <span>{formatInTz(m.date, tz, "compact")}</span>
              {m.sent_by_newsdesk && <span className="font-mono-display">{tr("· via Newsdesk")}</span>}
              {m.logged && <span className="text-emerald-800">{tr("· logged in PublishOS")}</span>}
            </div>
            <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-[var(--border)] p-4">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-[var(--accent)]" />
          {trRich("<0>Reply</0>", null, [<span className="overline" />])}
          {draft && <span className="text-[11px] text-[var(--ink-muted)]">{tr("draft v{version} · {value}", { version: draft.version, value: draft.language === "es" ? tr("Spanish") : tr("English") })}</span>}
          {(drafting.isPending || thread.draft_status === "working") && (
            <span className="text-[11px] text-[var(--ink-muted)] inline-flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" />{" "}{tr("drafting…")}</span>
          )}
        </div>

        {thread.draft_status === "error" && !draft && (
          <div className="mb-2 text-xs text-red-700 bg-red-50 border border-red-200 px-2.5 py-1.5">{tr("The draft failed: {draft_error}. Try again below, or write the reply yourself.", { draft_error: thread.draft_error || tr("unknown error") })}</div>
        )}

        {draft?.flags?.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {draft.flags.map((f) => (
              <span key={f} className="text-[11px] px-2 py-0.5 bg-amber-50 text-amber-900 border border-amber-200">
                {FLAG_LABELS[f] || f}
              </span>
            ))}
          </div>
        )}
        {draft?.rep_note && (
          <div className="mb-2 text-xs text-[var(--ink-muted)]">{trRich("<0>Note for you:</0> {rep_note}", { rep_note: draft.rep_note }, [<span className="font-medium" />])}</div>
        )}

        <textarea
          data-testid="prospect-reply-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={12}
          placeholder={drafting.isPending ? tr("Drafting…") : tr("Write the reply…")}
          className="w-full border border-[var(--border)] px-3 py-2 text-sm leading-relaxed outline-none focus:border-[var(--slate-900)] bg-white"
        />
        {connection?.signature && (
          <div className="text-xs text-[var(--ink-muted)] whitespace-pre-wrap mt-1 border-l-2 border-[var(--border)] pl-2">
            {connection.signature}
          </div>
        )}

        {draft?.recommended?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {draft.recommended.map((id) => offerById[id] && (
              <span key={id} className="text-[11px] px-2 py-0.5 border border-[var(--border)]">
                {offerById[id].name} · {money(offerById[id].rate ?? offerById[id].price)}
              </span>
            ))}
          </div>
        )}

        {badPrices.length > 0 && (
          <div data-testid="prospect-price-warning" className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 px-2.5 py-1.5">
            <div>{trRich("Not a PublishOS price: <0>{list}</0>. Fix it, or confirm it's right (for example a special price you agreed).", { list: badPrices.map(money).join(", ") }, [<span className="font-medium" />])}</div>
            <label className="flex items-center gap-1.5 mt-1">
              <input type="checkbox" checked={pricesOk} onChange={(e) => setPricesOk(e.target.checked)} />{tr("These prices are correct")}</label>
          </div>
        )}
        {data.checks?.unknown_links?.length > 0 && (
          <div className="mt-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 px-2.5 py-1.5">{tr("Links outside the paper and its store: {list}", { list: data.checks.unknown_links.join(", ") })}
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input
            data-testid="prospect-instruction"
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            placeholder={tr("Change it: \"shorter\", \"offer the 3-blast package\", \"in Spanish\"…")}
            className="flex-1 min-w-[220px] border border-[var(--border)] px-3 py-1.5 text-xs bg-white outline-none focus:border-[var(--slate-900)]"
          />
          <button
            data-testid="prospect-regenerate"
            onClick={() => drafting.mutate(instruction)}
            disabled={drafting.isPending}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-[var(--border)] hover:bg-[var(--secondary)] disabled:opacity-50"
          >
            {drafting.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {draft ? tr("Redraft") : tr("Draft a reply")}
          </button>
        </div>
      </div>

      <div className="bg-white border border-[var(--border)] p-4">
        <div className="overline mb-2">{tr("PublishOS")}</div>
        {!data.publishos ? (
          <p className="text-xs text-[var(--ink-muted)]">{tr("This property isn't linked to PublishOS, so nothing is logged.")}</p>
        ) : client ? (
          <div className="text-sm flex items-center gap-2 flex-wrap" data-testid="prospect-client-linked">
            <CheckCircle2 className="w-4 h-4 text-emerald-700" />
            <span className="font-medium">{client.business_name}</span>
            <span className="text-xs text-[var(--ink-muted)]">
              {client.is_advertiser ? tr("advertiser · {booking_count} booking(s)", { booking_count: client.booking_count }) : tr("prospect (no bookings yet)")}
              {client.sales_status ? ` · ${client.sales_status.replace(/_/g, " ")}` : ""}
              {client.assigned_sales_rep_name ? tr(" · rep {assigned_sales_rep_name}", { assigned_sales_rep_name: client.assigned_sales_rep_name }) : ""}
            </span>
            {connection?.rep_id && client.assigned_sales_rep_id && client.assigned_sales_rep_id !== connection.rep_id && (
              <span className="text-xs text-amber-900 bg-amber-50 border border-amber-200 px-2 py-0.5">{tr("Assigned to {assigned_sales_rep_name} in PublishOS", { assigned_sales_rep_name: client.assigned_sales_rep_name || tr("another rep") })}</span>
            )}
          </div>
        ) : (
          <ClientChooser match={data.match} choice={choice} setChoice={setChoice} />
        )}

        {data.publishos && (
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <label className="text-xs">
              {trRich("<0>Outcome</0>", null, [<span className="block text-[var(--ink-muted)] mb-1" />])}
              <select
                data-testid="prospect-outcome"
                value={outcome}
                onChange={(e) => setOutcome(e.target.value)}
                className="border border-[var(--border)] bg-white px-2 py-1.5 text-xs"
              >
                {(data.outcomes || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            {outcome !== "not_interested" && (
              <label className="text-xs">
                {trRich("<0>Follow up on</0>", null, [<span className="block text-[var(--ink-muted)] mb-1" />])}
                <input
                  data-testid="prospect-follow-up"
                  type="date"
                  value={followUp}
                  onChange={(e) => setFollowUp(e.target.value)}
                  className="border border-[var(--border)] bg-white px-2 py-1 text-xs"
                />
              </label>
            )}
            {connection && !connection.rep_id && (
              <span className="text-xs text-amber-900">{tr("No PublishOS rep on your mailbox yet:")}{" "}<Link to="/settings#sales-inbox" className="underline">{tr("set it in Settings")}</Link>.
              </span>
            )}
          </div>
        )}

        <div className="mt-4 flex items-center gap-3">
          <button
            data-testid="prospect-send"
            onClick={() => send.mutate()}
            disabled={sendDisabled}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {send.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}{tr("Send")}</button>
          <span className="text-xs text-[var(--ink-muted)]">
            {connection ? tr("From {address}", { address: connection.address }) : tr("The mailbox this came from is no longer connected.")}
          </span>
        </div>
      </div>
    </div>
  );
}

function ClientChooser({ match, choice, setChoice }) {
  if (!choice) return null;
  const options = [
    ...(match?.exact || []).map((c) => ({ ...c, why: tr("same email") })),
    ...(match?.suggestions || []).map((c) => ({ ...c, why: c.match === "domain" ? tr("same company domain") : tr("similar name") })),
  ];
  const f = choice.fields || {};
  const setField = (k, v) => setChoice({ mode: "create", fields: { ...f, [k]: v } });
  return (
    <div className="space-y-2 text-xs" data-testid="prospect-client-chooser">
      {options.map((c) => (
        <label key={c.id} className="flex items-center gap-2">
          <input
            type="radio"
            checked={choice.mode === "link" && choice.client_id === c.id}
            onChange={() => setChoice({ mode: "link", client_id: c.id, fields: f })}
          />
          {trRich("<0>Link to <1>{business_name}</1> <2>({why}{value}{value2}{value3})</2></0>", { business_name: c.business_name, why: c.why, value: c.email ? ` · ${c.email}` : "", value2: c.is_advertiser ? tr(" · advertiser") : "", value3: c.assigned_sales_rep_name ? tr(" · rep {assigned_sales_rep_name}", { assigned_sales_rep_name: c.assigned_sales_rep_name }) : "" }, [<span />, <span className="font-medium" />, <span className="text-[var(--ink-muted)]" />])}
        </label>
      ))}
      <label className="flex items-center gap-2">
        <input
          type="radio"
          checked={choice.mode === "create"}
          onChange={() => setChoice({ mode: "create", fields: f })}
        />
        {trRich("<0>Create a new client in PublishOS when I send</0>", null, [<span />])}
      </label>
      {choice.mode === "create" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pl-5">
          {[
            ["business_name", tr("Business name")], ["contact_name", tr("Contact name")],
            ["email", tr("Email")], ["phone", tr("Phone")], ["website", tr("Website")],
          ].map(([k, label]) => (
            <input
              key={k}
              data-testid={`prospect-client-${k}`}
              value={f[k] || ""}
              onChange={(e) => setField(k, e.target.value)}
              placeholder={label}
              className="border border-[var(--border)] px-2 py-1.5 text-xs bg-white outline-none focus:border-[var(--slate-900)]"
            />
          ))}
        </div>
      )}
      <label className="flex items-center gap-2">
        <input
          type="radio"
          checked={choice.mode === "none"}
          onChange={() => setChoice({ mode: "none", fields: f })}
        />
        {trRich("<0>Don't log this in PublishOS</0>", null, [<span />])}
      </label>
    </div>
  );
}
